/**
 * RC-156 — marcas que o envio de cada aviso automático TROCA de verdade (`replaceAll`
 * nos serviços de envio). Tudo que não está aqui segue literal até a cliente final.
 * Fonte única para a trava ao salvar (API) e para os botões "Inserir …" da tela.
 */
export const MESSAGE_TEMPLATE_TOKENS_BY_TYPE = {
    confirmation: ['{patient_name}', '{date}', '{time}', '{professional}', '{service}', '{clinic}'],
    reminder_24h: ['{patient_name}', '{date}', '{time}', '{professional}', '{service}', '{clinic}'],
    reminder_1h: ['{patient_name}', '{date}', '{time}', '{professional}', '{service}', '{clinic}'],
    followup: ['{patient_name}', '{service}', '{professional}'],
    cancellation: ['{patient_name}', '{professional}', '{reason}'],
    reactivation: ['{patient_name}', '{months}', '{clinic}'],
    birthday: ['{patient_name}', '{clinic}'],
    nps: ['{patient_name}', '{clinic}', '{link}'],
};
export const MESSAGE_TEMPLATE_TOKEN_LABELS = {
    '{patient_name}': 'Nome',
    '{date}': 'Data',
    '{time}': 'Horário',
    '{service}': 'Serviço',
    '{professional}': 'Profissional',
    '{clinic}': 'Clínica',
    '{reason}': 'Motivo',
    '{months}': 'Meses',
    '{link}': 'Link',
};
const MARKER_WORDS = [
    [/\b(nome|cliente|paciente|primeiro)\b/, '{patient_name}'],
    [/\b(data|dia)\b/, '{date}'],
    [/\b(hora|horario)\b/, '{time}'],
    [/\b(profissional|atendente)\b/, '{professional}'],
    [/\b(servico|procedimento|tratamento)\b/, '{service}'],
    [/\b(clinica|salao|empresa|estabelecimento)\b/, '{clinic}'],
    [/\bmotivo\b/, '{reason}'],
    [/\bmeses\b/, '{months}'],
    [/\blink\b/, '{link}'],
];
const ALL_KNOWN_TOKENS = new Set(Object.values(MESSAGE_TEMPLATE_TOKENS_BY_TYPE).flat());
function normalize(value) {
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}
function markerTokenFor(label) {
    const plain = normalize(label).replace(/_/g, ' ');
    return MARKER_WORDS.find(([pattern]) => pattern.test(plain))?.[1] ?? null;
}
function allowedFor(type) {
    return MESSAGE_TEMPLATE_TOKENS_BY_TYPE[type];
}
function suggestionFor(type, candidate) {
    return candidate && allowedFor(type).includes(candidate) ? candidate : null;
}
const BRACE_PATTERN = /\{+[^{}\n]{0,80}\}+/g;
const BRACKET_PATTERN = /\[([^[\]\n]{1,60})\]/g;
/** Marcas do texto que o envio deste aviso NÃO troca (sem repetição, na ordem em que aparecem). */
export function findInvalidMessageTemplateMarks(text, type) {
    const allowed = allowedFor(type);
    const found = [];
    for (const match of text.matchAll(BRACE_PATTERN)) {
        const mark = match[0];
        if (allowed.includes(mark))
            continue;
        const inner = mark.replace(/^\{+|\}+$/g, '');
        const canonical = `{${normalize(inner).replace(/\s+/g, '_')}}`;
        if (ALL_KNOWN_TOKENS.has(mark)) {
            found.push({ index: match.index ?? 0, mark, reason: 'other_type', suggestion: null });
        }
        else if (allowed.includes(canonical)) {
            found.push({ index: match.index ?? 0, mark, reason: 'format', suggestion: canonical });
        }
        else {
            found.push({ index: match.index ?? 0, mark, reason: 'unknown', suggestion: suggestionFor(type, markerTokenFor(inner)) });
        }
    }
    for (const match of text.matchAll(BRACKET_PATTERN)) {
        const candidate = markerTokenFor(match[1]);
        if (!candidate)
            continue;
        found.push({ index: match.index ?? 0, mark: match[0], reason: 'brackets', suggestion: suggestionFor(type, candidate) });
    }
    const seen = new Set();
    return found
        .sort((a, b) => a.index - b.index)
        .filter(({ mark }) => (seen.has(mark) ? false : (seen.add(mark), true)))
        .map(({ mark, reason, suggestion }) => ({ mark, reason, suggestion }));
}
export function isMessageTemplateTokenType(type) {
    return Object.prototype.hasOwnProperty.call(MESSAGE_TEMPLATE_TOKENS_BY_TYPE, type);
}
/** Frase de produto para a recusa: nomeia cada marca e o botão "Inserir …" certo. */
export function describeInvalidMessageTemplateMark(mark) {
    if (mark.suggestion) {
        return `${mark.mark}: o envio não troca essa marca; use o botão "Inserir ${MESSAGE_TEMPLATE_TOKEN_LABELS[mark.suggestion] ?? mark.suggestion}"`;
    }
    if (mark.reason === 'other_type')
        return `${mark.mark}: essa marca não existe neste aviso; apague`;
    return `${mark.mark}: o envio não troca essa marca; apague ou use um dos botões "Inserir …"`;
}
export function invalidMessageTemplateMarksMessage(marks) {
    return `O texto tem marcas que o envio não troca e chegariam assim para a cliente: ${marks.map(describeInvalidMessageTemplateMark).join('; ')}.`;
}
//# sourceMappingURL=message-template-marks.js.map